import { Ionicons } from '@expo/vector-icons';
import type { NativeStackHeaderProps } from 'expo-router';
import { DrawerActions } from 'expo-router/react-navigation';
import React from 'react';
import { View, Text, Pressable, StyleSheet } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useIsLargeScreen } from '@/hooks/use-is-large-screen';

export const ScreenHeader: React.FC<NativeStackHeaderProps> = ({ navigation, options }) => {
	const isLargeScreen = useIsLargeScreen();
	const insets = useSafeAreaInsets();

	if (options.headerShown === false) return null;

	const title = typeof options.headerTitle === 'string'
		? options.headerTitle
		: (options.title ?? '');

	// A screen can supply its own title element (React Navigation allows
	// `headerTitle: () => <Element/>`); previously anything non-string silently fell
	// back to `options.title`. A custom element gets LEFT-aligned, because centering
	// it next to an asymmetric headerLeft only looks centred by accident.
	const customTitle = typeof options.headerTitle === 'function'
		? options.headerTitle({ children: title })
		: null;

	// `headerLeft` sizes to its content rather than the 44pt icon slot, so a screen can
	// put a text back-link up here. Screens that don't set it keep the fixed slot, so
	// their layout is unchanged.
	const headerLeft = options.headerLeft?.({ canGoBack: navigation.canGoBack() });

	return (
		<View style={[styles.container, { paddingTop: insets.top }]}>
			<View style={styles.content}>
				<View style={[styles.left, headerLeft ? styles.leftAuto : null]}>
					{!isLargeScreen && (
						<Pressable
							onPress={() => navigation.dispatch(DrawerActions.toggleDrawer())}
							style={styles.button}
						>
							<Ionicons name="menu" size={24} color="#333" />
						</Pressable>
					)}
					{headerLeft}
				</View>
				{customTitle
					? <View style={styles.customTitle}>{customTitle}</View>
					: <Text style={styles.title} numberOfLines={1}>{title}</Text>}
				<View style={styles.right}>
					{options.headerRight?.({ canGoBack: navigation.canGoBack() })}
				</View>
			</View>
		</View>
	);
};

const styles = StyleSheet.create({
	container: {
		backgroundColor: '#fff',
		borderBottomWidth: StyleSheet.hairlineWidth,
		borderBottomColor: '#c8c8c8',
	},
	content: {
		// minHeight, not height: a two-line custom title needs room to grow. Identical
		// to the old fixed height for every single-line screen.
		minHeight: 44,
		flexDirection: 'row',
		alignItems: 'center',
		paddingHorizontal: 8,
	},
	left: {
		width: 44,
		alignItems: 'flex-start',
		justifyContent: 'center',
	},
	/** Content-sized left slot, for a text back-link rather than a 44pt icon. */
	leftAuto: {
		width: 'auto',
		flexDirection: 'row',
		alignItems: 'center',
	},
	customTitle: {
		flex: 1,
		// minWidth:0 lets this actually shrink on RN-Web instead of pushing the
		// headerRight slot off the end.
		minWidth: 0,
		justifyContent: 'center',
		paddingLeft: 8,
	},
	right: {
		width: 44,
		alignItems: 'flex-end',
		justifyContent: 'center',
	},
	title: {
		flex: 1,
		fontSize: 17,
		fontWeight: '600',
		color: '#000',
		textAlign: 'center',
	},
	button: {
		padding: 8,
	},
});
